import { Button } from "../../../components/ui/buttons/Button";
import { useState } from "react";
import { formatPhoneNumber } from "../../../utils/generalUtils";
import { useUser } from "../../../contexts/UserContext";
import { useLoading } from "../../../contexts/LoadingContext";
import { useSuccess } from "../../../contexts/SuccessContext";
import { useError } from "../../../contexts/ErrorContext";
// import useRunOptions from "../../../hooks/useRunOptions";
import { EmailInput, PhoneInput, TimeBeforeSelect } from "./components/NotificationInputs";
import Checkbox from "../../../components/ui/checkbox/Checkbox";
import { createPOINotificationAPI } from "../../../services/notificationsAPI";

const timeOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '15', value: 15 },
    { label: '20', value: 20 },
];

/**
 * Jan 20 -> moving to work on notification table
 * got to get the POIID to come in when opening the notification modal
 * 
 * thinking user can set multiple notifications at once - send in an array of POIID to set multiple
 * 
 */

export const NotificationDialog = ({ selectedJob, selectedPois, selectedRuns, setOpenNotificationDialog, fetchPOIData, selectedGroup}) => {
    const { user, setStoredJob } = useUser();
    const { showLoading, hideLoading } = useLoading();
    const { timeoutSuccess } = useSuccess();
    const { showError } = useError();
    // const { runNameOptions, fetchRuns } = useRunOptions();
    const [timeBeforeSelection, setTimeBeforeSelection] = useState([]);
    const [altEmail, setAltEmail] = useState(user?.Email || "");
    const [altPhone, setAltPhone] = useState(user?.Phone ? formatPhoneNumber(user.Phone) : "");
    const [isPhone, setIsPhone] = useState(true);
    const [isEmail, setIsEmail] = useState(true);
    // const [selectedRun, setSelectedRun] = useState();
    const [errors, setErrors] = useState({ email: "", phone: "" });
    const [userInputLocked, setUserInputLocked] = useState(true);

    const handleChangeContact = () => {
        setUserInputLocked(!userInputLocked);
    }

    const handleSaveNotification = async() => {
        /**
         * Need to get an object to send to API
         * {UserID, NTID, RunID, JobID, Interval, IsPhone, IsEmail, POIID, AltPhone, AltEmail}
         */
        try {
            // Guard against missing context/props early, before any logic
            if (!user) {
                showError("User context not initialized. Please refresh the page.");
                return;
            }

            if (errors.email || errors.phone) return;

            if (!timeBeforeSelection.length) {
                showError("You have not chosen a time before passage");

                return;
            }

            if(!selectedRuns || selectedRuns.length === 0) {
                showError("Please select a run for this notification");
                return;
            }

            if(!selectedPois || selectedPois?.length === 0) {
                showError("Please select POIs");
                return;
            }

            // Parse composite keys ("runId-poiId") into structured array
            const poiSelections = selectedPois.map(compositeKey => {
                const [runId, poiId] = compositeKey.split('-').map(Number);
                return { runId, poiId };
            });

            if(!isPhone && !isEmail) {
                showError("Please select at least one contact method (email, phone, or both) to receive notifications");
                return;
            }

            if(!user){
                hideLoading()
                showError("There was a problem saving notifications - no user.")
                return
            }

            const notifications = {
                intervals: timeBeforeSelection,
                SendTo: user.UserID,
                NTID: 2, //NTID is passage notification type
                JobID: selectedJob.value,
                CreatedBy: user.UserID,
                IsPhone: isPhone,
                IsEmail: isEmail,
                AltEmail: altEmail === user.Email ? null : altEmail, //if the user email is same as user data from db no need to update altPhone/altEmail
                AltPhone: altPhone === user.Phone ? null : altPhone, //backend chooses whether to send to alt or primary
                poiSelections: poiSelections, // Array of {runId, poiId} objects for granular control
                POIGroupID: selectedGroup.value
            }

            showLoading(`Saving Notifications...`);

            let response = await createPOINotificationAPI(notifications);
            if(response && response.success){
                hideLoading();
                timeoutSuccess('Notifications Saved');
                fetchPOIData();
                setOpenNotificationDialog(false);

                //update notification counts in storedJob (to decide if we show indicator that POI notifications need to be added for any runs)
                setStoredJob(prev => {
                    if (!prev || !prev.notificationCount) return prev;

                    // copy existing notificationCount
                    const updatedRuns = { ...prev.notificationCount };

                    // Build counts per RunID from response.data
                    const countsByRun = response.data.reduce((acc, notif) => {
                    const runId = notif.RunID;

                    if (!acc[runId]) acc[runId] = 0;
                        acc[runId] += 1;
                        return acc;
                    }, {});

                    // merge counts into notificationCount
                    Object.entries(countsByRun).forEach(([runId, count]) => {
                        const id = Number(runId);
                        if (updatedRuns[id]) {
                            updatedRuns[id] = {
                                ...updatedRuns[id],
                                POINotificationCount: count
                            };
                        } else {
                            updatedRuns[id] = {
                                RunID: id,
                                SpeedNotificationCount: 0,
                                POINotificationCount: count
                            };
                        }
                    });

                    console.log("updatedRuns")
                    console.log(updatedRuns)
                    return {
                        ...prev,
                        notificationCount: updatedRuns
                    };
                });
            } else {
                hideLoading();
                showError(response?.message || "Failed to save notifications");
            }
        } catch(error){
            hideLoading();
            console.error('Error saving notification:', error);
            showError(error?.message || "An unexpected error occurred while saving notifications");
        }

    };

    const handleCheckboxChange = (checkboxValue) => {
        if(checkboxValue.name === 'email-checkbox'){
            setIsEmail(checkboxValue.checked);
        } else {
            setIsPhone(checkboxValue.checked);
        }
    }

    const handleCancelClick = () => {
        //clear state
        setAltEmail("");
        setAltPhone("");
        setTimeBeforeSelection([]);
        //close dialog
        setOpenNotificationDialog(false);
    };

    return (
        <div>
            {/* Time Before Selection */}
            <TimeBeforeSelect
                timeOptions={timeOptions}
                timeBeforeSelection={timeBeforeSelection}
                setTimeBeforeSelection={setTimeBeforeSelection}
            />

            {/* Email and Phone Inputs */}
            <div className="border border-black p-5 mt-3 flex flex-col rounded-md">
                <h1 className="mb-2 text-lg font-bold">Notification will be sent to: </h1>
                <div className="flex">
                    <Checkbox
                        name='email-checkbox'
                        initiallyChecked={isEmail}
                        className="my-auto mr-2"
                        onChange={handleCheckboxChange}
                    />
                    <EmailInput
                        email={altEmail}
                        setEmail={setAltEmail}
                        errors={errors}
                        setErrors={setErrors}
                        disabled={userInputLocked}
                    />
                </div>
                <div className="flex mt-2">
                    <Checkbox
                        name='phone-checkbox'
                        initiallyChecked={isPhone}
                        className="my-auto mr-2"
                        onChange={handleCheckboxChange}
                    />
                    <PhoneInput
                        phone={altPhone}
                        setPhone={setAltPhone}
                        errors={errors}
                        setErrors={setErrors}
                        disabled={userInputLocked}
                    />
                </div>
                {userInputLocked &&
                    <button
                        className="mx-auto mt-2 text-blue-800"
                        onClick={handleChangeContact}
                    >
                        Change Contact
                    </button>
                }
                {!userInputLocked &&
                    <button
                        className="mx-auto mt-2 text-blue-800"
                        onClick={handleChangeContact}
                    >
                        Save
                    </button>
                }
            </div>

            {/* Buttons */}
            <div className="flex justify-around mt-10">
                <Button onClick={handleSaveNotification}>Save Notification</Button>
                <Button variant="danger" onClick={handleCancelClick}>
                    Cancel
                </Button>
            </div>
        </div>
    );
};
